import { useAppSelector } from "../../state/store";
import { Fragment, useMemo, useState } from "react";
import { StringInput } from "../Inputs";
import { PlusMinusButton } from "../PlusMinusButton";
import { BookQuickLinks, ChapterList } from "./ChapterSelector";
import { Row } from "../Row";
import { chapterSelected } from "../../util/generalUtil";
import { GamePlayer } from "../../types/generalTypes";
import { Link } from "react-router";

interface GameSettingProps<E> {
  title: string;
  description?: string;
  hasMaterial?: boolean | ((settings: E) => boolean);
  minQuizzers?: number;
  extraSettings?: {
    defaultSettings: E;
    settingsUI: (
      settings: E,
      setSettings: (newSettings: E) => void
    ) => React.ReactNode;
  };

  gamePage: string;
  settingsKey?: string;
  onStart?: (players: GamePlayer[], settings: E) => void;
}

export function GameSettings<E>(props: GameSettingProps<E>) {
  const settings = useAppSelector((state) => state.settings);
  const getDefaultPlayers = () => {
    const players = settings.quizNowQuizzers.map((q) => ({
      name: q,
      material: settings.selectedChapters,
    }));
    for (let i = players.length; i < (props.minQuizzers || 1); i++) {
      players.push({
        name: "",
        material: settings.selectedChapters,
      });
    }
    return players;
  };
  const [players, setPlayers] = useState<GamePlayer[]>(getDefaultPlayers());
  const [playerMaterialViewing, setPlayerMaterialViewing] = useState(-1);

  const [gameSettings, setGameSettings] = useState(
    props.extraSettings?.defaultSettings
  );

  const hasMaterial = useMemo(() => {
    if (!props.hasMaterial) return false;
    if (typeof props.hasMaterial === "boolean") return props.hasMaterial;
    return props.hasMaterial(gameSettings as E);
  }, [props.hasMaterial, gameSettings]);

  const startEnabled = useMemo(() => {
    let newEnabled = true;
    players.forEach((quizzer) => {
      if (!quizzer.name) newEnabled = false;
    });
    if (props.minQuizzers && props.minQuizzers > players.length) return false;
    if (hasMaterial && players.some((p) => !chapterSelected(p.material)))
      return false;
    return newEnabled;
  }, [players, props.minQuizzers, hasMaterial]);

  const h2 = (
    <h2
      style={{
        margin: 0,
        fontSize: 28,
        marginBottom: 12,
      }}
    >
      {props.title}
    </h2>
  );
  if (playerMaterialViewing >= 0) {
    return (
      <div className="settings-container">
        <div className="settings-top-sticky">
          {h2}
          <Row>
            <button
              className="secondary-btn"
              onClick={() => setPlayerMaterialViewing(-1)}
              style={{ marginBottom: 10 }}
            >
              Go Back
            </button>
            <span style={{ marginLeft: 10, marginTop: 5 }}>{`Material for ${
              players[playerMaterialViewing].name || "unnamed quizzer"
            }`}</span>
          </Row>
          <BookQuickLinks />
        </div>
        <ChapterList
          allowVerseSelection
          selected={players[playerMaterialViewing].material}
          onChange={(material) => {
            const newPlayers = [...players];
            newPlayers[playerMaterialViewing] = {
              ...players[playerMaterialViewing],
              material,
            };
            setPlayers(newPlayers);
          }}
        />
      </div>
    );
  }
  return (
    <div style={{ marginLeft: "auto", marginRight: "auto", maxWidth: 440 }}>
      {h2}
      {props.description && <p>{props.description}</p>}
      <div>
        {players.map((item, index) => (
          <Fragment key={`quizzer-${index}`}>
            <StringInput
              caption={`Quizzer ${index + 1}:`}
              value={item.name}
              onChange={(value) => {
                const result = [...players];
                result[index] = { ...item, name: value };
                setPlayers(result);
              }}
              afterInput={
                <div style={{ display: "flex", width: 40 }}>
                  {(index > 0 || players.length > 1) && (
                    <div style={{ marginLeft: 5 }}>
                      <PlusMinusButton
                        onClick={() => {
                          const newList = [...players];
                          newList.splice(index, 1);
                          setPlayers(newList);
                        }}
                        ariaLabel="Remove Player"
                      >
                        -
                      </PlusMinusButton>
                    </div>
                  )}
                  {players.length === index + 1 && (
                    <div style={{ marginLeft: 5 }}>
                      <PlusMinusButton
                        onClick={() => {
                          const newList = [...players];
                          newList.push({
                            name: "",
                            material: settings.selectedChapters,
                          });
                          setPlayers(newList);
                        }}
                        ariaLabel="Add Player"
                      >
                        +
                      </PlusMinusButton>
                    </div>
                  )}
                </div>
              }
            />
            {hasMaterial && (
              <p>
                Select
                <button
                  className="link"
                  onClick={() => setPlayerMaterialViewing(index)}
                >
                  Material
                </button>
                {item.name ? ` for ${item.name}` : ""}
              </p>
            )}
          </Fragment>
        ))}
      </div>
      {gameSettings &&
        props.extraSettings?.settingsUI?.(gameSettings, setGameSettings)}
      <div style={{ display: "flex", marginTop: 15, marginBottom: 15 }}>
        {startEnabled ? (
          <Link
            to={props.gamePage}
            onClick={() => props.onStart?.(players, gameSettings!)}
            style={{ marginRight: 15 }}
            state={
              props.settingsKey
                ? { [props.settingsKey]: { players, settings: gameSettings } }
                : undefined
            }
            className="clickable"
          >
            Start
          </Link>
        ) : (
          <span className="disabled-clickable" style={{ marginRight: 15 }}>
            Start
          </span>
        )}
        <Link className="secondary-btn" to="..">
          Go Back
        </Link>
      </div>
    </div>
  );
}
